// frontend/src/components/CostEstimation/EstimationSidebar.jsx
import React, { useState } from 'react';
import { Input, Select, InputNumber, Button, Upload, message, Collapse, Divider } from 'antd';
import { UploadOutlined, WarningOutlined, SaveOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { estimationFieldsConfig } from '../../config/estimationConfig';

const { Option } = Select;
const { Panel } = Collapse;

const EstimationSidebar = ({
  projectInputs,
  onInputChange,
  masterProjectOptions,
  masterProjectId,
  onMasterProjectChange,
  inflationBases,
  inflationBasis,
  onInflationBasisChange,
  onCSVUpload,
  onMengenFileUpload,
  mengenSheets,
  mengenSheet,
  onMengenSheetChange,
  csvUploadErrors,
  onSave,
  isSaving,
  lastSaveTime,
  hasUnsavedChanges,
  estimationName
}) => {
  const [uploading, setUploading] = useState(false);

  /**
   * Render a single input field based on configuration
   */
  const renderField = (fieldKey, fieldConfig) => {
    const value = projectInputs[fieldKey];

    const handleChange = (newValue) => {
      onInputChange(fieldKey, newValue);
    };

    switch (fieldConfig.type) {
      case 'select':
        return (
          <Select
            value={value || undefined}
            onChange={handleChange}
            placeholder={fieldConfig.placeholder || `${fieldConfig.label} auswählen`}
            style={{ width: '100%' }}
            allowClear
          >
            {fieldConfig.options?.map(option => (
              <Option key={option} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        );

      case 'number':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <InputNumber
              value={value}
              onChange={handleChange}
              placeholder={fieldConfig.placeholder}
              style={{ flex: 1 }}
              min={0}
            />
            {fieldConfig.unit && (
              <span style={{ fontSize: '12px', color: '#666' }}>
                {fieldConfig.unit}
              </span>
            )}
          </div>
        );

      case 'text':
      default:
        return (
          <Input
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={fieldConfig.placeholder}
          />
        );
    }
  };

  /**
   * Handle file selection: SIA 416 Flächen workbook (.xlsx) is parsed on
   * the backend; CSV (eBKP,Menge) keeps the legacy client-side path.
   */
  const handleFileChange = (file) => {
    console.log('File selected:', file);

    if (!file) {
      message.error('Datei konnte nicht gelesen werden');
      return false;
    }

    if (/\.xlsx?$/i.test(file.name)) {
      setUploading(true);
      Promise.resolve(onMengenFileUpload(file)).finally(() => setUploading(false));
      return false;
    }

    setUploading(true);

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target.result;
        console.log('File content:', content);
        onCSVUpload(content);
      } catch (error) {
        console.error('Error reading CSV:', error);
        message.error('Fehler beim Lesen der CSV-Datei');
      } finally {
        setUploading(false);
      }
    };

    reader.onerror = () => {
      console.error('FileReader error');
      message.error('Fehler beim Lesen der Datei');
      setUploading(false);
    };

    reader.readAsText(file);

    // Prevent default upload behavior
    return false;
  };

  /**
   * Handle master project change
   */
  const handleMasterProjectSelect = (projectId) => {
    onMasterProjectChange(projectId);
  };

  /**
   * Handle save button click
   */
  const handleSaveClick = () => {
    onSave();
  };

  // Get only enabled fields
  const enabledFields = Object.entries(estimationFieldsConfig)
    .filter(([_, config]) => config.enabled);

  return (
    <div style={{ padding: '20px', height: '100vh', overflowY: 'auto' }}>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
          Projektinformationen
        </h2>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {enabledFields.map(([fieldKey, fieldConfig]) => (
          <div key={fieldKey}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                color: '#666',
                marginBottom: '6px',
                fontWeight: 500
              }}
            >
              {fieldConfig.label}
              {fieldConfig.required && (
                <span style={{ color: '#ff4d4f', marginLeft: '4px' }}>*</span>
              )}
            </label>
            {renderField(fieldKey, fieldConfig)}
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: '24px',
          padding: '12px',
          background: '#f6f7f1',
          border: '1px solid #e6e9dc',
          borderRadius: '10px',
          fontSize: '12px',
          color: '#5d6457'
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: '4px' }}>
          Hinweis
        </div>
        <div>
          Die Werte stammen aus Ihrer Suche und können hier angepasst werden.
        </div>
      </div>

      {/* Master Project Selector */}
      <div
        style={{
          marginTop: '24px',
          padding: '16px',
          background: '#fff',
          border: '1px solid #e6e9dc',
          borderRadius: '10px'
        }}
      >
        <label
          style={{
            display: 'block',
            fontSize: '13px',
            color: '#000',
            marginBottom: '8px',
            fontWeight: 600
          }}
        >
          Master Projekt
        </label>
        <Select
          value={masterProjectId}
          onChange={handleMasterProjectSelect}
          placeholder="— Keine Auswahl —"
          style={{ width: '100%' }}
          allowClear
        >
          <Option key="null-option" value={null}>
            — Keine Auswahl —
          </Option>

          {masterProjectOptions && masterProjectOptions.map(project => (
            <Option key={project.projectId} value={project.projectId}>
              {project.projectName}
            </Option>
          ))}
        </Select>
        <div
          style={{
            marginTop: '6px',
            fontSize: '11px',
            color: '#999',
            fontStyle: 'italic'
          }}
        >
          Setzt Preis/Einheit für alle nicht-manuell bearbeiteten Zeilen
        </div>
      </div>

      {/* Inflation (ZIW index basis) Section */}
      <div
        style={{
          marginTop: '16px',
          padding: '16px',
          background: '#fff',
          border: '1px solid #e6e9dc',
          borderRadius: '10px'
        }}
      >
        <label
          style={{
            display: 'block',
            fontSize: '13px',
            color: '#000',
            marginBottom: '8px',
            fontWeight: 600
          }}
        >
          Indexbasis (Teuerung)
        </label>
        <Select
          value={inflationBasis}
          onChange={onInflationBasisChange}
          placeholder="— Keine —"
          style={{ width: '100%' }}
          allowClear
        >
          <Option key="none-option" value={null}>
            — Keine —
          </Option>
          {inflationBases && inflationBases.map(base => (
            <Option key={base.basis} value={base.basis}>
              {base.label}
            </Option>
          ))}
        </Select>
        <div
          style={{
            marginTop: '6px',
            fontSize: '11px',
            color: '#999',
            fontStyle: 'italic'
          }}
        >
          {inflationBasis
            ? `Preise/Einheit aus der Suche werden vom Jahr der Position (Spalte "Jahr") bis ${
                (inflationBases.find(b => b.basis === inflationBasis) || {}).latest_year || 'heute'
              } mit dem ZIW-Index hochgerechnet`
            : 'Ohne Auswahl stammen die Preise direkt aus der Suche'}
        </div>
      </div>

      {/* CSV Upload Section */}
      <div
        style={{
          marginTop: '16px',
          padding: '16px',
          background: '#fff',
          border: '1px solid #e6e9dc',
          borderRadius: '10px'
        }}
      >
        <label
          style={{
            display: 'block',
            fontSize: '13px',
            color: '#000',
            marginBottom: '8px',
            fontWeight: 600
          }}
        >
          Mengen importieren
        </label>

        <Upload
          beforeUpload={handleFileChange}
          accept=".xlsx,.csv"
          showUploadList={false}
          maxCount={1}
        >
          <Button
            icon={<UploadOutlined />}
            loading={uploading}
            block
            style={{ marginBottom: '8px' }}
          >
            Mengen importieren (Excel/CSV)
          </Button>
        </Upload>

        <div
          style={{
            fontSize: '11px',
            color: '#999',
            fontStyle: 'italic',
            marginBottom: '8px'
          }}
        >
          SIA 416 Flächen-Excel oder CSV (eBKP,Menge)
        </div>

        {/* Sheet selector for the parsed SIA 416 workbook */}
        {mengenSheets && mengenSheets.length > 0 && (
          <div style={{ marginTop: '8px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                color: '#666',
                marginBottom: '6px',
                fontWeight: 500
              }}
            >
              Blatt
            </label>
            <Select
              value={mengenSheet}
              onChange={onMengenSheetChange}
              style={{ width: '100%' }}
            >
              {mengenSheets.map(sheet => (
                <Option key={sheet.name} value={sheet.name}>
                  {sheet.name}
                </Option>
              ))}
            </Select>
            <div
              style={{
                marginTop: '6px',
                fontSize: '11px',
                color: '#999',
                fontStyle: 'italic'
              }}
            >
              Werte aus der Spalte "Gesamttotal" des Blatts. Beim Blattwechsel
              werden die Mengen passender Einheiten überschrieben — auch
              manuell geänderte.
            </div>
          </div>
        )}

        {/* Display skipped codes if any */}
        {csvUploadErrors && csvUploadErrors.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            <Collapse
              ghost
              defaultActiveKey={csvUploadErrors.length <= 5 ? ['1'] : []}
              style={{
                background: '#fff2e8',
                border: '1px solid #ffbb96',
                borderRadius: '4px'
              }}
            >
              <Panel
                header={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <WarningOutlined style={{ color: '#fa8c16' }} />
                    <span style={{ fontSize: '12px', fontWeight: 500, color: '#d46b08' }}>
                      {csvUploadErrors.length} {csvUploadErrors.length === 1 ? 'Fehler' : 'Fehler'} beim Import
                    </span>
                  </div>
                }
                key="1"
              >
                <div
                  style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    fontSize: '11px',
                    color: '#d46b08'
                  }}
                >
                  {csvUploadErrors.map((error, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '4px 0',
                        borderBottom: index < csvUploadErrors.length - 1 ? '1px solid #ffe7ba' : 'none'
                      }}
                    >
                      {error}
                    </div>
                  ))}
                </div>
              </Panel>
            </Collapse>
          </div>
        )}
      </div>

      {/* Divider before Save section */}
      <Divider style={{ margin: '24px 0' }} />

      {/* Save Section */}
      <div
        style={{
          padding: '16px',
          background: hasUnsavedChanges ? '#fff7e6' : '#f3f7e2',
          border: `1px solid ${hasUnsavedChanges ? '#ffd591' : '#dde8b4'}`,
          borderRadius: '10px'
        }}
      >
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#000', marginBottom: '4px' }}>
            Speicherstatus
          </div>
          {estimationName && (
            <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
              Name: <strong>{estimationName}</strong>
            </div>
          )}
          {lastSaveTime && (
            <div style={{ fontSize: '11px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <ClockCircleOutlined />
              Zuletzt gespeichert: {lastSaveTime.toLocaleString('de-CH', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          )}
          {hasUnsavedChanges && (
            <div style={{ fontSize: '11px', color: '#fa8c16', marginTop: '4px', fontStyle: 'italic' }}>
              ⚠ Ungespeicherte Änderungen
            </div>
          )}
          {!hasUnsavedChanges && estimationName && (
            <div style={{ fontSize: '11px', color: '#52c41a', marginTop: '4px', fontStyle: 'italic' }}>
              ✓ Alle Änderungen gespeichert
            </div>
          )}
        </div>

        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSaveClick}
          loading={isSaving}
          block
          size="large"
          style={{
            background: hasUnsavedChanges ? '#fa8c16' : '#52c41a',
            borderColor: hasUnsavedChanges ? '#fa8c16' : '#52c41a'
          }}
        >
          {estimationName ? 'Speichern' : 'Erste Speicherung'}
        </Button>

        <div
          style={{
            marginTop: '8px',
            fontSize: '11px',
            color: '#999',
            fontStyle: 'italic',
            textAlign: 'center'
          }}
        >
          Auto-Speicherung alle 5 Sekunden
        </div>
      </div>

      {/* Info Box */}
      <div
        style={{
          marginTop: '16px',
          padding: '12px',
          background: '#f3f7e2',
          border: '1px solid #e9f0cd',
          borderRadius: '10px',
          fontSize: '11px',
          color: '#4c650f'
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: '4px' }}>
          💡 Tipp
        </div>
        <div>
          Verwenden Sie das Lesezeichen (✓) um abgeschlossene Positionen zu markieren und zu sperren.
          Diese werden beim erneuten Laden nicht überschrieben.
        </div>
      </div>
    </div>
  );
};

export default EstimationSidebar;